import crawlPolicyData from "../data/crawl-policy-data.json";
import { LEGACY_CRAWL_CANONICAL_PATHS } from "./canonical-redirects";
import { PRIVATE_CRAWL_PREFIXES } from "./private-route-policy";
import { legacyCanonicalPath } from "../../edge-db/shared/internal-href-canonical";

// Removed templated families answer 410 Gone; the rule table and its rationale
// live in the dependency-free module the static exporter also imports.
export { goneFamilyHubPath, isGonePath } from "../../edge-db/shared/gone-paths";

export { LEGACY_CRAWL_CANONICAL_PATHS } from "./canonical-redirects";

const QUERY_CANONICAL_EXEMPT_PREFIXES = [
  "/api",
  "/cart",
  "/checkout",
  "/checkouts",
  "/ops",
  "/pay",
] as const;

function canonicalPath(pathname: string) {
  if (pathname === "/") return "/";
  if ((pathname.split("/").pop() ?? "").includes(".")) return pathname;
  return pathname.endsWith("/") ? pathname : `${pathname}/`;
}

function matchesPrefix(pathname: string, prefix: string) {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * Non-content utility surfaces: iframe embeds, widget endpoints and the app-store
 * redirect. These are not pages and must not enter the index. Hardcoded on purpose —
 * there is no SEO policy layer any more. Delete this list to index them.
 *
 * Also the seven regional `/<bn|gu|kn|ml|mr|ta|te>/free-kundali/` editions
 * (2026-09-23 sitemap audit). Each is ~250 words of translated intro and FAQ
 * around the English-only kundali form, promising a chart "in Bengali/Tamil/…"
 * that the tool cannot produce (it renders en or hi only), and the seven are one
 * template. They earned 12 impressions in total over Jul–Sep. They stay served
 * and linked (`noindex, follow`), and `regionalLanguageEntries` already drops a
 * noindex edition from every hreflang cluster. Remove a path here once its
 * edition renders the form and the chart in its own language.
 */
const PUBLIC_NOINDEX_PATHS = new Set<string>(crawlPolicyData.publicNoindexExactPaths);

export function isPublicNoindexPath(pathname: string) {
  return PUBLIC_NOINDEX_PATHS.has(canonicalPath(pathname));
}

export function isPrivateCrawlPath(pathname: string) {
  return PRIVATE_CRAWL_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix));
}

export function isQueryCanonicalizationExemptPath(pathname: string) {
  return QUERY_CANONICAL_EXEMPT_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix));
}

/**
 * Pattern-shaped legacy redirects, for families too large to enumerate as exact
 * paths (year-qualified Rashifal/transit URLs, muhurat month slices, and counting).
 *
 * These belong in the SAME authority as the exact map because `pathDisposition`
 * resolves `redirect` before every noindex branch: a path claimed here can never
 * also be claimed as `noindex,follow`. Before 2026-07-30 the muhurat months were
 * declared BOTH ways — a noindex pattern list (removed 2026-09-30) said serve-200-noindex while
 * `middleware.ts` 301'd them — and the static publisher believed the contract, so
 * it seeded 169 URLs it then fetched as HTTP 301. That single disagreement was
 * 57% of a quarantine that blocked every publish for two days. The same table
 * also keeps app middleware, both content Workers, sitemap filtering, and R2
 * pruning aligned when a retired year-qualified object still exists in storage.
 */
export function legacyCrawlCanonicalPath(pathname: string) {
  return legacyCanonicalPath(pathname, LEGACY_CRAWL_CANONICAL_PATHS);
}

export function legacyPolicyCanonicalPath(pathname: string) {
  const canonical = legacyCrawlCanonicalPath(pathname);
  return pathname.startsWith("/policies/") ? canonical : null;
}

export function shouldInjectRouteJsonLd(pathname: string) {
  const normalized = canonicalPath(pathname);
  return !isPrivateCrawlPath(normalized) && !isPublicNoindexPath(normalized);
}
